import { memo, useMemo, useState } from "react";
import { useNavigateCarrying } from "@/lib/carryParams";
import { useT } from "@/lib/i18n";
import type { SiteSnapshot, SiteTopology } from "@/data/types";
import { signedKW, unsignedKW, IDLE_DEADBAND_W } from "@/lib/format";
import { GridNode, FactoryNode, BessCabinet, SolarArray, EmsHub } from "./nodes";
import {
  buildLayout, curvedPath, magnitudeToWidth,
  type ScadaLayout, type ScadaVariant, type Port, type ZoneId,
} from "./layout";
import { scada } from "./scadaColors";

/**
 * Site SCADA — bus-centric energy flow.
 *
 *   ┌──────────────────┐    ┌─────────────────────────┐    ┌──────────────┐
 *   │     Grid         │    │      ☀ PV (top)         │    │   Facility   │
 *   │   coupling       │ ◀━━●━━━━━━━━━●━━━━━━━━━━━━━━●━━━━▶│    loads     │
 *   │                  │    │  ▣ EMS         │           │ │              │
 *   └──────────────────┘    │                ▼           │ └──────────────┘
 *                           │           ▮ BESS fleet ▮   │
 *                           └────────────────────────────┘
 *
 * Flow lines are direct between the physical actors and the common bus —
 * the EMS is just a small controller chip sitting ON the bus with a
 * dashed control link to the BESS trunk. Stroke width is sqrt-scaled by
 * |kW|; idle flows (<500 W noise floor) render as static dashes.
 */
export function SiteFlowDiagram({
  topology,
  snap,
  variant = "compact",
  detail = true,
  onDetailChange,
}: {
  topology: SiteTopology;
  snap: SiteSnapshot;
  variant?: ScadaVariant;
  /** Detailed (true, default) shows the secondary instrument strips +
   *  edge pills; Summary (false) collapses them to magnitude+direction
   *  only. Default true reproduces today's view exactly. */
  detail?: boolean;
  /** When provided, renders the Summary|Detailed segmented control and
   *  calls this on toggle. Omitted (e.g. ScadaTestPage) → no control. */
  onDetailChange?: (v: boolean) => void;
}) {
  const t = useT();
  // Carrying navigate: node drill-downs keep the operator's current time
  // window when one is set, falling back to a 24h preset only when none
  // exists (LINK-13 — a hardcoded preset used to discard the window).
  const navigate = useNavigateCarrying();
  const drillToHistorical = (keys: string) => {
    const hasWindow = new URLSearchParams(window.location.search).has("start");
    navigate("/historical", hasWindow ? { keys } : { keys, preset: "24h" });
  };
  const layout = useMemo(() => buildLayout(topology, variant), [topology, variant]);
  // Render-stable decorative density: derived ONLY from the variant prop
  // (not from live flow values), so it never changes across per-tick
  // re-renders and the FlowParticles SMIL memo / begin-stagger stay intact.
  const density: "lean" | "rich" = variant === "compact" ? "lean" : "rich";
  const [hover, setHover] = useState<string | null>(null);

  const gridW = snap.grid.active_W;
  const bessW = snap.bess_aggregate.active_W;
  const pvW = snap.pv?.active_W ?? 0;
  const loadW = snap.building_load_W;

  // TIME-INVARIANT stroke-width reference. Derived solely from nameplate
  // (topology BESS max charge/discharge + PV rated) plus a 100 kW grid
  // floor — never from live flows, so a given flow renders the same width
  // at 3am and noon (no "breathing" width that would reset the running
  // marching/particle animations on every snapshot tick).
  const refKW = useMemo(() => {
    const fleetKW = topology.bess.reduce(
      (s, b) => s + Math.max(b.max_charge_kW, b.max_discharge_kW), 0);
    const pvKW = snap.pv?.rated_kWp ?? 0;
    const GRID_FLOOR_KW = 100; // grid has no nameplate in SiteTopology; floor prevents a div-by-small ref and grid-line re-saturation
    return Math.max(fleetKW, pvKW, GRID_FLOOR_KW);
  }, [topology, snap.pv?.rated_kWp]);

  // Unified idle noise floor (format.ts:IDLE_DEADBAND_W) — flows below
  // this magnitude render as static dashes, not animated arrows, so
  // sensor noise / inverter standby never reads as a live power flow.
  const gridImport = gridW > IDLE_DEADBAND_W;
  const gridExport = gridW < -IDLE_DEADBAND_W;
  const bessCharge = bessW > IDLE_DEADBAND_W;
  const bessDischarge = bessW < -IDLE_DEADBAND_W;
  const pvActive = pvW > IDLE_DEADBAND_W;
  // The "loads" branch is bidirectional now that net building load
  // can go negative (PV surplus exceeds consumption). When positive
  // the bus feeds the loads; when negative the loads push surplus
  // back onto the bus (which then becomes part of grid export).
  const loadConsume = loadW > IDLE_DEADBAND_W;
  const loadExport = loadW < -IDLE_DEADBAND_W;
  const loadActive = loadConsume || loadExport;

  return (
    <div className="relative w-full overflow-hidden rounded-xl bg-surface-2/40 ring-1 ring-edge">
      {/* Summary | Detailed segmented control — only when the host wires
          onDetailChange (ScadaTestPage passes neither, so it shows none).
          print:hidden keeps exported diagrams clean. */}
      {onDetailChange && (
        <div
          role="group"
          aria-label={t("monitor.scada.density.label")}
          className="pointer-events-auto absolute right-2 top-2 z-10 flex items-center gap-px rounded-md bg-surface-2/70 p-px text-[10px] ring-1 ring-edge backdrop-blur print:hidden"
        >
          <button
            type="button"
            aria-pressed={!detail}
            onClick={() => onDetailChange(false)}
            className={`rounded px-2 py-1 font-medium leading-none transition ${
              !detail ? "bg-accent-soft text-accent-strong" : "text-text-muted hover:text-text"
            }`}
          >
            {t("monitor.scada.density.summary")}
          </button>
          <button
            type="button"
            aria-pressed={detail}
            onClick={() => onDetailChange(true)}
            className={`rounded px-2 py-1 font-medium leading-none transition ${
              detail ? "bg-accent-soft text-accent-strong" : "text-text-muted hover:text-text"
            }`}
          >
            {t("monitor.scada.density.detailed")}
          </button>
        </div>
      )}
      <svg
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        className="block h-auto w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <Defs />

        {/* Zone backdrops */}
        <ZoneBackdrop layout={layout} variant={variant} />

        {/* Bus spine — the label comes verbatim from the topology; when a
            site doesn't declare one we render a plain "SITE BUS" with no
            voltage/frequency claim (OVW-10). */}
        <BusSpine layout={layout} label={topology.bus_label ?? "SITE BUS"} />

        {/* Energy flow connections — all drawn BEFORE nodes so the
            nodes layer on top of the line endpoints. */}
        <Connections
          layout={layout}
          refKW={refKW}
          density={density}
          detail={detail}
          gridW={gridW} bessW={bessW} pvW={pvW} loadW={loadW}
          gridImport={gridImport} gridExport={gridExport}
          bessCharge={bessCharge} bessDischarge={bessDischarge}
          pvActive={pvActive} loadActive={loadActive}
          loadConsume={loadConsume} loadExport={loadExport}
        />

        {/* Grid coupling (front-of-meter) — clicking drills into the
            Historical analyzer with grid power preselected. */}
        <g
          onMouseEnter={() => setHover("grid")}
          onMouseLeave={() => setHover(null)}
          onClick={() => drillToHistorical("grid_W")}
          style={{ cursor: "pointer" }}
        >
          <GridNode cx={layout.grid.cx} cy={layout.grid.cy} snap={snap}
            importing={gridImport} exporting={gridExport} detail={detail} />
        </g>

        {/* Site loads (behind-meter) — clicking takes the operator to
            the Historical analyzer with the full power-flow set
            preselected (grid + battery + pv if present). */}
        <g
          onMouseEnter={() => setHover("load")}
          onMouseLeave={() => setHover(null)}
          onClick={() => {
            const keys = [
              "grid_W",
              "battery/aggregation/power",
              ...(layout.pv.length > 0 ? ["pv_power_W"] : []),
            ].join(",");
            drillToHistorical(keys);
          }}
          style={{ cursor: "pointer" }}
        >
          <FactoryNode cx={layout.factory.cx} cy={layout.factory.cy} snap={snap} detail={detail} />
        </g>

        {/* Solar arrays — one node per topology PV. */}
        {snap.pv && layout.pv.map((pv, i) => {
          const share = layout.pv.length;
          return (
            <g
              key={pv.id ?? `pv-${i}`}
              onMouseEnter={() => setHover("pv")}
              onMouseLeave={() => setHover(null)}
              onClick={() => drillToHistorical("pv_power_W")}
              style={{ cursor: "pointer" }}
            >
              <SolarArray
                cx={pv.cx} cy={pv.cy}
                active_W={snap.pv!.active_W / share}
                today_kWh={snap.pv!.today_kWh / share}
                rated_kWp={snap.pv!.rated_kWp / share}
                label={pv.label}
                detail={detail}
              />
            </g>
          );
        })}

        {/* EMS controller chip — sits ON the bus, NOT on the energy
            path. The control link is rendered separately below as a
            dashed grey line to the BESS trunk. */}
        <g onClick={() => navigate("/controller")}>
          <EmsHub
            cx={layout.hub.cx} cy={layout.hub.cy} r={layout.hub.r}
            mode={snap.controller.active_mode}
            tracking={snap.controller.pid_actively_controlling}
          />
        </g>

        {/* BESS fleet */}
        <BessFleet
          topology={topology}
          snap={snap}
          layout={layout}
          density={density}
          onHover={(id) => setHover(id ? `bess:${id}` : null)}
          onUnitClick={(id) => navigate(`/bess/${id}`)} // carrying navigate — window survives the drill-down
        />
      </svg>

      <NodeTooltip nodeKey={hover} snap={snap} topology={topology} />

      <div className="pointer-events-none absolute bottom-2 left-3 flex flex-wrap items-center gap-3 text-[10px] text-text-muted">
        <Legend color={scada.gridImport()} label={t("monitor.scada.legend.gridImport")} />
        {layout.pv.length > 0 && <Legend color={scada.solar()} label={t("monitor.scada.legend.pv")} />}
        <Legend color={scada.charge()} label={t("monitor.scada.legend.bessCharge")} />
        <Legend color={scada.discharge()} label={t("monitor.scada.legend.bessDischarge")} />
        <Legend color={scada.gridExport()} label={t("monitor.scada.legend.loadExport")} />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Defs                                                                       */
/* -------------------------------------------------------------------------- */
function Defs() {
  return (
    <defs>
      {/* Chevron-style arrowheads with a soft halo so direction reads at
          a glance even on small viewports. */}
      {ARROW_DEFS.map((a) => (
        <marker
          key={a.id}
          id={a.id}
          viewBox="0 0 14 14"
          refX="12"
          refY="7"
          markerWidth="11"
          markerHeight="11"
          orient="auto-start-reverse"
        >
          <path d="M1,1 L13,7 L1,13 L4,7 Z" fill={a.color()} opacity={0.25} />
          <path d="M2,2 L13,7 L2,12 L5,7 Z" fill={a.color()} />
        </marker>
      ))}
      <filter id="soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" style={{ floodColor: "var(--scada-shadow)" }} floodOpacity="1" />
      </filter>
      {/* Soft glow for the flow particles. */}
      <filter id="flow-particle-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1.4" />
      </filter>
    </defs>
  );
}

/* -------------------------------------------------------------------------- */
/* Zone backdrops                                                             */
/* -------------------------------------------------------------------------- */
/** Stable zone id → i18n catalog key. Resolution happens here in the
 *  component layer so buildLayout() stays locale-agnostic and pure. */
const ZONE_LABEL_KEY: Record<ZoneId, string> = {
  frontOfMeter: "monitor.scada.zone.frontOfMeter",
  onSite: "monitor.scada.zone.onSite",
  behindMeter: "monitor.scada.zone.behindMeter",
};

function ZoneBackdrop({ layout, variant }: { layout: ScadaLayout; variant: ScadaVariant }) {
  const t = useT();
  // Neutral, quiet dividers — no data hues — so the front/on-site/behind
  // partition reads as structure, not as a flow colour competing with the
  // violet/blue/green flow vocabulary. --edge-strong keeps the dashed border
  // visible even on the black theme; --text-faint at 0.025 is a barely-there wash.
  const zones = [layout.zones.frontOfMeter, layout.zones.onSite, layout.zones.behindMeter];
  return (
    <g pointerEvents="none">
      {zones.map((z) => {
        const label = t(ZONE_LABEL_KEY[z.id]);
        return (
          <g key={z.id}>
            <title>{label}</title>
            <rect x={z.x} y={z.y} width={z.w} height={z.h} rx={16}
              fill="var(--text-faint)" fillOpacity={0.025}
              stroke="var(--edge-strong)" strokeOpacity={0.5} strokeWidth={1}
              strokeDasharray="6 6" />
            {variant === "full" && (
              <text x={z.x + 14} y={z.y + 18}
                fontSize={8.5} fontWeight={700} fill="var(--text-muted)"
                opacity={0.6} letterSpacing={1.6}>
                {label.toUpperCase()}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}

/* -------------------------------------------------------------------------- */
/* Bus spine                                                                  */
/* -------------------------------------------------------------------------- */
function BusSpine({ layout, label }: { layout: ScadaLayout; label: string }) {
  // Tap points where on-site flows enter / leave the bus — drawn as
  // visible junction dots so the operator immediately reads "everything
  // on the site connects to this rail." Grid and load taps are styled
  // separately (squared terminals) below.
  const taps: { x: number; key: string }[] = [];
  layout.pv.forEach((pv, i) => taps.push({ x: pv.cx, key: `pv-${i}` }));
  taps.push({ x: layout.bus.bessTapX, key: "bess" });

  // Label sits ON the rail in the clear span to the right of the BESS
  // trunk — well clear of the EMS chip, PV downcomers and BESS flow
  // pill which all live on the left half.
  const labelX = (layout.bus.bessTapX + layout.bus.x2) / 2;
  const labelY = layout.bus.y;
  // Pill auto-sizes to the label (topology bus_label is free text).
  const pillW = Math.max(72, label.length * 7.5 + 20);
  return (
    <g pointerEvents="none">
      {/* Soft shadow under the rail — gives the busbar a 3D feel and
          makes it read as a physical conductor sitting in front of the
          zone backdrops. */}
      <line
        x1={layout.bus.x1} y1={layout.bus.y + 3}
        x2={layout.bus.x2} y2={layout.bus.y + 3}
        stroke="var(--scada-shadow)" strokeWidth={6.5}
        strokeLinecap="round"
      />
      {/* Bus rail — the visible backbone. Solid, thick, full opacity so
          the spine connecting grid → on-site → loads reads at a glance. */}
      <line
        x1={layout.bus.x1} y1={layout.bus.y}
        x2={layout.bus.x2} y2={layout.bus.y}
        stroke="var(--text-faint)" strokeWidth={7}
        strokeLinecap="round" opacity={0.85}
      />
      {/* Thin top highlight — 3D busbar sheen. */}
      <line
        x1={layout.bus.x1} y1={layout.bus.y - 2}
        x2={layout.bus.x2} y2={layout.bus.y - 2}
        stroke="var(--surface)" strokeOpacity={0.55}
        strokeWidth={1.6} strokeLinecap="round"
      />
      {/* End terminals — squared lugs hint that the rail extends out to
          the grid (left) and to the loads (right), tinted with the
          respective zone colour. */}
      <rect x={layout.bus.x1 - 6} y={layout.bus.y - 6} width={12} height={12} rx={2}
        fill="var(--surface)" stroke={scada.gridImport()} strokeWidth={1.8} />
      <rect x={layout.bus.x2 - 6} y={layout.bus.y - 6} width={12} height={12} rx={2}
        fill="var(--surface)" stroke={scada.gridExport()} strokeWidth={1.8} />
      {/* On-site junction taps (PV downcomers + BESS trunk). */}
      {taps.map(t => (
        <circle key={t.key} cx={t.x} cy={layout.bus.y} r={4.5}
          fill="var(--surface)" stroke="var(--text-faint)" strokeWidth={1.6} />
      ))}
      {/* Bus label — sits on the rail in the clear right span. Rendered
          verbatim from topology.bus_label (or the claim-free fallback). */}
      <g transform={`translate(${labelX} ${labelY})`}>
        <rect x={-pillW / 2} y={-9} width={pillW} height={18} rx={3}
          fill="var(--surface)" stroke="var(--edge-strong)" strokeWidth={0.9} />
        <text x={0} y={4} textAnchor="middle"
          fontSize={10} fontWeight={700} letterSpacing={1.4}
          fill="var(--text-muted)">
          {label}
        </text>
      </g>
    </g>
  );
}

/* -------------------------------------------------------------------------- */
/* Connections                                                                */
/* -------------------------------------------------------------------------- */
interface ConnectionsProps {
  layout: ScadaLayout;
  refKW: number;
  density: "lean" | "rich";
  detail: boolean;
  gridW: number; bessW: number; pvW: number; loadW: number;
  gridImport: boolean; gridExport: boolean;
  bessCharge: boolean; bessDischarge: boolean;
  pvActive: boolean; loadActive: boolean;
  loadConsume: boolean; loadExport: boolean;
}

function Connections(props: ConnectionsProps) {
  const t = useT();
  const { layout } = props;
  // Grid (right edge of grid plate) → bus left endpoint.
  const gridFrom: Port = { x: layout.grid.cx + 78, y: layout.grid.cy };
  const gridTo: Port = { x: layout.bus.x1, y: layout.bus.y };
  // Bus right endpoint → factory (left edge of plate).
  const loadFrom: Port = { x: layout.bus.x2, y: layout.bus.y };
  const loadTo: Port = { x: layout.factory.cx - 78, y: layout.factory.cy };
  // PV: each array drops a vertical line to its own tap on the bus.
  const pvShareW = layout.pv.length > 0 ? props.pvW / layout.pv.length : 0;
  // BESS aggregate trunk: bus tap → top-centre of fleet.
  const bessFrom: Port = { x: layout.bus.bessTapX, y: layout.bus.y };
  const bessTo: Port = {
    x: layout.bessGrid.cx,
    y: layout.bessGrid.origin.y - 6,
  };

  return (
    <g pointerEvents="none">
      {/* Grid ↔ Bus — always draw source→destination based on the
          actual flow direction. The chevron at the path's END always
          points TOWARDS the destination, matching the operator's
          intuition ("the arrow shows where energy is going"). When
          exporting, we draw the path bus→grid; when importing or
          idle, we draw it grid→bus. */}
      <FlowLine
        from={props.gridExport ? gridTo : gridFrom}
        to={props.gridExport ? gridFrom : gridTo}
        magnitude={Math.abs(props.gridW)}
        refKW={props.refKW}
        density={props.density}
        stroke={props.gridExport ? scada.gridExport() : scada.gridImport()}
        arrow={props.gridImport ? "arrow-import" : props.gridExport ? "arrow-export" : undefined}
      />

      {/* Bus ↔ Loads — bidirectional. Net building load > 0 means
          flow goes bus → loads (consumption); < 0 means the loads are
          a net producer pushing surplus back onto the bus. The
          chevron always sits at the destination, matching the
          source→destination convention used elsewhere. */}
      <FlowLine
        from={props.loadExport ? loadTo : loadFrom}
        to={props.loadExport ? loadFrom : loadTo}
        magnitude={Math.abs(props.loadW)}
        refKW={props.refKW}
        density={props.density}
        stroke={props.loadExport ? scada.solar() : scada.gridExport()}
        arrow={props.loadExport ? "arrow-load-rev" : "arrow-load"}
        skipIfIdle={!props.loadActive}
      />

      {/* Per-PV downcomer — vertical to a distinct tap on the bus.
          No flow pill: the SolarArray plate's own footer shows the kW
          generated, so an extra pill on the line just clutters the
          space when arrays sit close together. The arrow chevron + the
          dashed/animated line are enough to communicate direction. */}
      {layout.pv.map((pv, i) => {
        const pvFrom: Port = { x: pv.cx, y: pv.cy + 50 };
        const pvTo: Port = { x: pv.cx, y: layout.bus.y };
        return (
          <FlowLine
            key={pv.id ?? `pv-${i}`}
            from={pvFrom}
            to={pvTo}
            magnitude={Math.abs(pvShareW)}
            refKW={props.refKW}
            density={props.density}
            stroke={scada.solar()}
            arrow="arrow-solar"
            labelTone={scada.solar()}
            skipIfIdle={!props.pvActive}
            orientation="vertical"
          />
        );
      })}

      {/* BESS aggregate trunk: drawn source→destination per the active
          flow direction so the chevron at the END always points TOWARD
          the destination. Charging: bus→fleet (chevron points down,
          into the battery). Discharging: fleet→bus (chevron points
          up, into the bus). Idle: bus→fleet by convention, no arrow. */}
      <FlowLine
        from={props.bessDischarge ? bessTo : bessFrom}
        to={props.bessDischarge ? bessFrom : bessTo}
        magnitude={Math.abs(props.bessW)}
        refKW={props.refKW}
        density={props.density}
        stroke={props.bessCharge ? scada.charge() : props.bessDischarge ? scada.discharge() : scada.idle()}
        arrow={props.bessCharge ? "arrow-charge" : props.bessDischarge ? "arrow-discharge" : undefined}
        label={`${signedKW(props.bessW)} kW`}
        labelSub={props.bessCharge ? t("monitor.scada.charge") : props.bessDischarge ? t("monitor.scada.discharge") : t("monitor.scada.idle")}
        labelTone={props.bessCharge ? scada.charge() : props.bessDischarge ? scada.discharge() : scada.idle()}
        labelPlacement="right"
        orientation="vertical"
        showLabel={props.detail}
      />

      {/* Manifold from trunk to each cabinet column */}
      <BessManifold
        layout={layout}
        active={props.bessCharge || props.bessDischarge}
        tone={props.bessCharge ? scada.charge() : props.bessDischarge ? scada.discharge() : scada.idle()}
        reverse={props.bessDischarge}
      />

      {/* EMS → BESS control link (signal only, dashed, no chevron). */}
      <EmsControlLink layout={layout} />
    </g>
  );
}

function EmsControlLink({ layout }: { layout: ScadaLayout }) {
  // Short dashed signal link from the EMS chip's bottom edge to a
  // visible tap on the BESS trunk — communicates "the EMS issues
  // setpoints to the BESS", without claiming kilowatts pass this way.
  const from: Port = { x: layout.hub.cx, y: layout.hub.cy + layout.hub.r + 4 };
  const trunkTapY = (layout.bus.y + (layout.bessGrid.origin.y - 6)) / 2;
  const to: Port = { x: layout.bessGrid.cx - 4, y: trunkTapY };
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const c1: Port = { x: from.x, y: from.y + Math.abs(dy) * 0.5 };
  const c2: Port = { x: to.x - dx * 0.3, y: to.y };
  const d = `M${from.x},${from.y} C${c1.x},${c1.y} ${c2.x},${c2.y} ${to.x},${to.y}`;
  return (
    <g pointerEvents="none">
      <path d={d}
        fill="none" stroke={scada.brand()}
        strokeOpacity={0.55}
        strokeWidth={1} strokeDasharray="2 4"
      />
      {/* Tiny terminator dot at the BESS-trunk end of the link. */}
      <circle cx={to.x} cy={to.y} r={2}
        fill="var(--surface)" stroke={scada.brand()} strokeOpacity={0.7} strokeWidth={0.9} />
    </g>
  );
}

function BessManifold({
  layout, active, tone, reverse,
}: { layout: ScadaLayout; active: boolean; tone: string; reverse?: boolean }) {
  const g = layout.bessGrid;
  if (g.cols === 1) return null;
  const trunkY = g.origin.y - 14;
  const x1 = g.origin.x + g.cellW / 2;
  const x2 = g.origin.x + g.totalW - g.cellW / 2;
  return (
    <g pointerEvents="none">
      <line x1={x1} y1={trunkY} x2={x2} y2={trunkY}
        stroke={tone} strokeOpacity={active ? 0.85 : 0.45}
        strokeWidth={active ? 2.4 : 1.6}
        strokeLinecap="round" />
      {Array.from({ length: g.cols }).map((_, i) => {
        const x = g.origin.x + i * g.cellW + g.cellW / 2;
        return (
          <line key={i}
            x1={x} y1={trunkY}
            x2={x} y2={g.origin.y + 4}
            className={active ? `flow-line thin${reverse ? " rev" : ""}` : "flow-line idle thin"}
            stroke={tone}
            strokeWidth={active ? 2.0 : 1.4}
            fill="none"
          />
        );
      })}
      {Array.from({ length: g.cols }).map((_, i) => {
        const x = g.origin.x + i * g.cellW + g.cellW / 2;
        return (
          <circle
            key={`cap-${i}`}
            cx={x} cy={trunkY} r={active ? 2.6 : 1.8}
            fill="var(--surface)"
            stroke={tone}
            strokeOpacity={active ? 0.95 : 0.5}
            strokeWidth={1.2}
          />
        );
      })}
    </g>
  );
}

/**
 * One flow line between two ports with:
 *   - a smooth bezier path,
 *   - magnitude-scaled stroke width,
 *   - march animation when above the idle threshold,
 *   - moving "energy packet" particles riding the curve,
 *   - a label pill positioned OFF the line (so the line and text never
 *     overlap).
 */
function FlowLine({
  from, to, magnitude, refKW, stroke, arrow, arrowReverse, reverse,
  label, labelSub, labelTone, skipIfIdle,
  labelPlacement = "above",
  orientation = "horizontal",
  density = "rich",
  showLabel = true,
}: {
  from: Port; to: Port; magnitude: number; refKW: number; stroke: string;
  arrow?: string; arrowReverse?: string; reverse?: boolean;
  label?: string;
  labelSub?: string;
  labelTone?: string;
  skipIfIdle?: boolean;
  /** Mount/unmount gate for the label pill (Summary mode collapses it). */
  showLabel?: boolean;
  /** Where to place the label relative to the line midpoint. */
  labelPlacement?: "above" | "below" | "left" | "right";
  /** Curve major axis. Horizontal = sweep mainly in X; Vertical = in Y. */
  orientation?: "horizontal" | "vertical";
  /** Decorative density: 'lean' = 1 particle (compact), 'rich' = 3. */
  density?: "lean" | "rich";
}) {
  // Quantise everything that feeds an animation attribute. The grid/BESS/PV
  // magnitudes fluctuate by ~kW on every snapshot tick; if the derived stroke
  // width and the <animateMotion dur> / particle radius tracked that jitter
  // continuously, React would rewrite those attributes every render and the
  // browser would re-resolve the RUNNING animation — the visible "stuck
  // moment" where the marching dashes / flow particles reset each tick.
  //
  // We snap the ANIMATION OUTPUTS (not the input magnitude) to coarse steps,
  // which is scale-independent: a ±kW wobble shifts `dur` by < 0.01 s, far
  // inside a 0.2 s bucket, so the rendered attribute strings stay byte-for-byte
  // identical across ticks and React leaves the live animation untouched. The
  // precise kW still shows in the label (plain text, safe to update each tick).
  const w = Math.round(magnitudeToWidth(magnitude, refKW) * 2) / 2; // → 0.5 px steps
  const kw = magnitude / 1000;                               // magnitude is |W|
  const animDur = Math.round(Math.max(1.0, 3.6 - Math.log10(Math.max(1, kw)) * 0.9) / 0.2) * 0.2;
  // Particle radius tracks line width, but on a GENTLE, well-floored curve.
  // The old `w * 0.85` mapping ran [2.4 … 4.8] px, so a near-idle flow — e.g.
  // a grid-zero-controlled site whose grid line sits just above the deadband
  // (w = 2) — got 2.4 px dots that read as "much smaller" next to the
  // high-power lines' 4.8 px balls. Remapping w ∈ [2, 8] → core ∈ [3.6, 4.8]
  // keeps the "bigger flow ⇒ bigger packet" cue while making low-power
  // packets clearly visible and consistent with the rest. (Quantised to
  // 0.4 px so the value is stable across snapshot ticks — see animDur.)
  const animCore = Math.round((3.6 + (w - 2) * 0.2) / 0.4) * 0.4;
  const animate = magnitude > IDLE_DEADBAND_W;
  const d = curvedPath(from, to, {
    tension: 0.45,
    horizontalFirst: orientation === "horizontal",
  });
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  return (
    <g>
      {/* Soft glow underlay — only when active, gives the line a sense of "energised" */}
      {animate && (
        <path d={d}
          stroke={stroke}
          strokeWidth={w + 6}
          fill="none"
          opacity={0.10}
          strokeLinecap="round"
          pointerEvents="none"
        />
      )}
      <path
        d={d}
        className={`flow-line ${!animate ? "idle" : ""} ${reverse ? "rev" : ""}`}
        stroke={stroke}
        strokeWidth={w}
        fill="none"
        markerEnd={arrow ? `url(#${arrow})` : undefined}
        markerStart={arrowReverse ? `url(#${arrowReverse})` : undefined}
      />
      {animate && (
        <FlowParticles
          pathD={d}
          dur={animDur}
          core={animCore}
          color={stroke}
          reverse={reverse}
          count={density === "lean" ? 1 : 3}
        />
      )}
      {label && !skipIfIdle && showLabel && (
        <FlowLabel
          x={midX} y={midY}
          placement={labelPlacement}
          label={label}
          sub={labelSub}
          tone={labelTone ?? stroke}
        />
      )}
    </g>
  );
}

/**
 * Pill label rendered OFF the flow line with a small leader dot — so the
 * arrow line and the text never overlap. Placement picks an offset
 * perpendicular to the line orientation.
 */
function FlowLabel({
  x, y, placement, label, sub, tone,
}: {
  x: number; y: number;
  placement: "above" | "below" | "left" | "right";
  label: string;
  sub?: string;
  tone: string;
}) {
  // Pill geometry — auto-size to the text.
  const padX = 8;
  const padY = sub ? 4 : 3;
  const labelLen = label.length;
  const subLen = (sub ?? "").length;
  const charW = 6.5;
  const pillW = Math.max(
    labelLen * charW,
    subLen * 5.4,
  ) + padX * 2;
  const pillH = sub ? 30 : 18;

  // Offset perpendicular to the placement.
  let dx = 0, dy = 0;
  const off = 18;
  if (placement === "above") dy = -off - pillH / 2;
  else if (placement === "below") dy = off + pillH / 2;
  else if (placement === "left") dx = -off - pillW / 2;
  else if (placement === "right") dx = off + pillW / 2;

  const px = x + dx;
  const py = y + dy;

  return (
    <g pointerEvents="none">
      {/* Leader dot at the line midpoint */}
      <circle cx={x} cy={y} r={2.4} fill={tone} />
      {/* Subtle leader line from the dot to the pill edge */}
      <line
        x1={x} y1={y}
        x2={px + (dx < 0 ? pillW / 2 : dx > 0 ? -pillW / 2 : 0)}
        y2={py + (dy < 0 ? pillH / 2 : dy > 0 ? -pillH / 2 : 0)}
        stroke={tone} strokeOpacity={0.40} strokeWidth={0.8}
        strokeDasharray="2 2"
      />
      {/* Pill background */}
      <rect
        x={px - pillW / 2} y={py - pillH / 2}
        width={pillW} height={pillH} rx={4}
        fill="var(--surface)"
        stroke={tone}
        strokeOpacity={0.50}
        strokeWidth={0.9}
        filter="url(#soft-shadow)"
      />
      {/* Magnitude */}
      <text
        x={px} y={sub ? py - 1 : py + 4}
        textAnchor="middle"
        fontSize={11}
        fontWeight={800}
        fill={tone}
        className="num"
      >
        {label}
      </text>
      {/* Direction sub-label */}
      {sub && (
        <text
          x={px} y={py + 11}
          textAnchor="middle"
          fontSize={8}
          fontWeight={600}
          letterSpacing={0.8}
          fill="var(--text-muted)"
        >
          {sub.toUpperCase()}
        </text>
      )}
    </g>
  );
}

/**
 * Energy packets travelling along the flow line — the SCADA-classic
 * "moving dots" pattern that makes power-flow direction read
 * instantly.
 *
 * `dur` (particle period) and `core` (dot radius) arrive ALREADY quantised
 * to coarse steps from {@link FlowLine}. Because those props only change when
 * the flow crosses a bucket boundary, this memoised subtree is value-equal
 * across the parent's per-tick re-renders — React skips it entirely and the
 * live SMIL `<animateMotion>` keeps running uninterrupted instead of being
 * reset every snapshot tick.
 */
const FlowParticles = memo(function FlowParticles({
  pathD, dur, core, color, reverse, count,
}: {
  pathD: string;
  dur: number;
  core: number;
  color: string;
  reverse?: boolean;
  count: number;
}) {
  const halo = core * 2.3;
  const fromTo = reverse ? "1;0" : "0;1";
  return (
    <g pointerEvents="none" className="flow-particles">
      {Array.from({ length: count }).map((_, i) => (
        <g key={i} className="flow-particle">
          <animateMotion
            dur={`${dur}s`}
            repeatCount="indefinite"
            begin={`${-(dur / count) * i}s`}
            path={pathD}
            keyPoints={fromTo}
            keyTimes="0;1"
            calcMode="linear"
          />
          <circle r={halo} fill={color} opacity={0.20} filter="url(#flow-particle-glow)" />
          <circle r={core} fill={color} opacity={0.95} />
        </g>
      ))}
    </g>
  );
});

/* -------------------------------------------------------------------------- */
/* BESS fleet (1..N cabinets in a grid)                                       */
/* -------------------------------------------------------------------------- */
function BessFleet({
  topology, snap, layout, density, onHover, onUnitClick,
}: {
  topology: SiteTopology;
  snap: SiteSnapshot;
  layout: ScadaLayout;
  density: "lean" | "rich";
  onHover: (id: string | null) => void;
  onUnitClick: (id: string) => void;
}) {
  const g = layout.bessGrid;
  const visibleUnits = snap.bess_per_unit.slice(0, g.visibleCount);

  // Fleet summary chip lives below the cabinets.
  const summaryX = g.cx;
  const summaryY = g.origin.y + g.totalH + 60;
  const summaryText = `${topology.bess.length}× BESS · ${snap.bess_aggregate.online_count} online · ${snap.bess_aggregate.soc_avg_pct.toFixed(0)}% avg SOC`;

  return (
    <g>
      {visibleUnits.map((u, i) => {
        const col = i % g.cols;
        const row = Math.floor(i / g.cols);
        const x = g.origin.x + col * g.cellW + (g.cellW - g.cabW) / 2;
        const y = g.origin.y + row * g.cellH;
        const cfg = topology.bess.find((b) => b.id === u.id);
        return (
          <g
            key={u.id}
            onMouseEnter={() => onHover(u.id)}
            onMouseLeave={() => onHover(null)}
            onClick={() => onUnitClick(u.id)}
          >
            <BessCabinet
              x={x} y={y}
              w={g.cabW} h={g.cabH}
              unit={u}
              family={cfg?.family ?? "generic"}
              capacity_kWh={cfg?.capacity_kWh ?? 0}
              density={density}
            />
          </g>
        );
      })}

      <g transform={`translate(${summaryX} ${summaryY})`}>
        <rect x={-118} y={-9} width={236} height={18} rx={3}
          fill="var(--surface)" stroke="var(--edge)" strokeWidth={0.6} />
        <text x={0} y={3} textAnchor="middle"
          fontSize={9.5} fontWeight={600}
          fill="var(--text-muted)">
          {summaryText}{g.overflowCount > 0 ? ` · +${g.overflowCount} more` : ""}
        </text>
      </g>
    </g>
  );
}

/* -------------------------------------------------------------------------- */
/* Tooltip + helpers                                                          */
/* -------------------------------------------------------------------------- */
function NodeTooltip({
  nodeKey, snap, topology,
}: { nodeKey: string | null; snap: SiteSnapshot; topology: SiteTopology }) {
  const t = useT();
  if (!nodeKey) return null;
  let title = "";
  let rows: [string, string][] = [];
  if (nodeKey === "grid") {
    title = t("monitor.scada.tip.gridTitle");
    rows = [
      [t("monitor.scada.tip.activePower"), `${signedKW(snap.grid.active_W)} kW`],
      [t("monitor.flow.reactive"), `${(snap.grid.reactive_VAr / 1000).toFixed(1)} kVAr`],
      [t("monitor.flow.frequency"), `${snap.grid.frequency_Hz.toFixed(2)} Hz`],
      [t("monitor.flow.voltage"), `${snap.grid.voltage_V.toFixed(1)} V`],
      [t("monitor.scada.node.powerFactor"), snap.grid.pf.toFixed(2)],
    ];
  } else if (nodeKey === "load") {
    title = t("monitor.scada.tip.loadsTitle");
    rows = [
      [t("monitor.now.netBuildingLoad"), `${signedKW(snap.building_load_W)} kW`],
      [t("monitor.flow.selfSuffToday"), `${snap.energy_today.self_sufficiency_pct.toFixed(0)}%`],
      [t("monitor.flow.selfConsToday"), `${snap.energy_today.self_consumption_pct.toFixed(0)}%`],
    ];
  } else if (nodeKey === "pv" && snap.pv) {
    title = t("monitor.scada.node.solarPv");
    rows = [
      [t("monitor.now.generating"), `${unsignedKW(snap.pv.active_W)} kW`],
      [t("monitor.flow.today"), `${snap.pv.today_kWh.toFixed(0)} kWh`],
      [t("monitor.flow.rated"), `${snap.pv.rated_kWp.toFixed(0)} kWp`],
      [t("monitor.scada.tip.capacityFactor"), `${Math.round((snap.pv.active_W / Math.max(1, snap.pv.rated_kWp * 1000)) * 100)}%`],
    ];
  } else if (nodeKey.startsWith("bess:")) {
    const id = nodeKey.slice(5);
    const u = snap.bess_per_unit.find((x) => x.id === id);
    const cfg = topology.bess.find((b) => b.id === id);
    if (u && cfg) {
      // Title = "{device} · {family}" — both raw topology data, kept untranslated.
      title = `${u.name} · ${cfg.family}`;
      rows = [
        [t("monitor.bess.power"), `${signedKW(u.active_W)} kW`],
        ["SOC", `${u.soc_pct.toFixed(1)}%`],
        [t("monitor.bess.dcBus"), `${u.total_voltage_V.toFixed(0)} V`],
        [t("analyze.bessDetail.temp"), `${u.ambient_temperature_C.toFixed(1)} °C`],
        [t("monitor.scada.tip.status"),
          u.online
            ? (u.fault_count > 0 ? t("monitor.scada.tip.faults", { n: u.fault_count }) : t("status.online"))
            : t("status.offline")],
      ];
    }
  }
  if (!title) return null;
  return (
    <div className="pointer-events-none absolute right-3 top-3 z-10 max-w-[260px] rounded-md border border-edge bg-bg/95 px-3 py-2 text-[11px] shadow-xl backdrop-blur">
      <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{title}</div>
      <div className="mt-1 space-y-0.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <span className="text-text-muted">{k}</span>
            <span className="num font-semibold text-text">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

// `color` is a thunk: arrowhead fills must be resolved at render time
// (inside `Defs`) so they re-read the `--ems-*` vars when the theme flips.
//
// `arrow-export` and `arrow-discharge` are the "flipped flow direction"
// counterparts of import/charge. The flow lines for export/discharge are
// now drawn source→destination (path reversed), so we use a normal
// markerEnd in the corresponding semantic colour. The `-rev` ids are
// kept to avoid breaking any external reference; they're equivalent.
const ARROW_DEFS = [
  { id: "arrow-import",        color: scada.gridImport },
  { id: "arrow-export",        color: scada.gridExport },
  { id: "arrow-import-rev",    color: scada.gridExport },
  { id: "arrow-charge",        color: scada.charge },
  { id: "arrow-discharge",     color: scada.discharge },
  { id: "arrow-discharge-rev", color: scada.discharge },
  { id: "arrow-solar",         color: scada.solar },
  { id: "arrow-load",          color: scada.gridExport },
  // Reverse-flow loads (PV surplus pushing back onto the bus) —
  // coloured like solar so the visual language reads "extra solar
  // is going out through the load branch".
  { id: "arrow-load-rev",      color: scada.solar },
];
